import { createContext, useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useContent, DEFAULT_CONTENT } from '../hooks/useContent';

// Live Sales Hub content (from Supabase, falling back to src/data/salesPlaybook.js)
const PlaybookContext = createContext(DEFAULT_CONTENT);
function usePlaybook() {
  const c = useContext(PlaybookContext);
  return {
    COMPANY:         c.company,
    PRICING_TIERS:   c.pricing_tiers,
    VALUE_ADDS:      c.value_adds,
    ICP:             c.icp,
    TRUSTED_BY:      c.trusted_by,
    OBJECTIONS:      c.objections,
    EMAIL_TEMPLATES: c.email_templates,
    CALL_SCRIPTS:    c.call_scripts,
    QUICK_FACTS:     c.quick_facts
  };
}

const KES = (n) => 'KES ' + Number(n).toLocaleString('en-KE');

// Pull {{variable}} tokens out of a template string. Returns unique, in-order.
function extractVars(text) {
  const found = [];
  const re = /\{\{\s*([a-z0-9_]+)\s*\}\}/gi;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (!found.includes(m[1])) found.push(m[1]);
  }
  return found;
}

function applyVars(text, values) {
  return text.replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (_, k) => values[k] || `{{${k}}}`);
}

function prettifyVar(v) {
  return v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

async function copy(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function SalesHub() {
  const { content } = useContent();
  return (
    <PlaybookContext.Provider value={content}>
    <div className="bg-slate-50">
      <Hero />
      <QuickFactsBand />
      <section className="max-w-7xl mx-auto px-4 py-10 space-y-12">
        <PricingTiers />
        <ValueAdds />
        <Section title="Ideal Client Profile" subtitle="Where Binary One wins fastest.">
          <IcpGrid />
        </Section>
        <Section title="Trusted By" subtitle="Drop these names early in conversations.">
          <TrustedBy />
        </Section>
        <Section
          title="Email Templates"
          subtitle="Pick one, fill the variables, copy, send."
        >
          <EmailTemplates />
        </Section>
        <Section
          title="Call Scripts"
          subtitle="Cold calls, voicemails, gatekeepers, discovery."
        >
          <CallScripts />
        </Section>
        <Section
          title="Objection Handling"
          subtitle="The five we hear over and over — and how to answer."
        >
          <Objections />
        </Section>
        <ContactStrip />
      </section>
    </div>
    </PlaybookContext.Provider>
  );
}

/* --- Sections --- */

function Hero() {
  const { COMPANY } = usePlaybook();
  return (
    <div className="bg-gradient-to-br from-ink-900 via-ink-700 to-brand-700 text-white">
      <div className="max-w-7xl mx-auto px-4 py-10 md:py-14">
        <div className="flex items-start justify-between gap-6 flex-col md:flex-row">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-brand-100/90 mb-3">
              <span className="bg-brand-500 text-ink-900 rounded px-2 py-0.5 font-bold">B1S</span>
              <span>{COMPANY.name} · Sales Hub</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-bold leading-tight">{COMPANY.slogan}</h1>
            <p className="mt-3 text-slate-200 text-sm md:text-base">{COMPANY.positioning}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/pipeline" className="btn bg-brand-500 hover:bg-brand-400 text-ink-900 font-semibold">
                Open Pipeline →
              </Link>
              <Link to="/dashboard" className="btn bg-white/10 hover:bg-white/20 text-white">
                View Dashboard
              </Link>
              <a
                href={`mailto:${COMPANY.email}`}
                className="btn bg-white/10 hover:bg-white/20 text-white"
              >
                {COMPANY.email}
              </a>
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-sm space-y-1 min-w-[260px] border border-brand-500/30">
            <div className="font-semibold text-brand-300">Free IT Audit Offer</div>
            <p className="text-slate-200 text-xs">{COMPANY.cta}</p>
            <div className="pt-2 border-t border-white/20 text-xs text-slate-300">
              <div>📍 {COMPANY.address}</div>
              <div>📞 {COMPANY.phone}</div>
              <div>🌐 binaryone.co.ke</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickFactsBand() {
  const { QUICK_FACTS } = usePlaybook();
  return (
    <div className="bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        {QUICK_FACTS.map((f) => (
          <div key={f.label} className="text-center">
            <div className="text-2xl md:text-3xl font-bold text-brand-700">{f.value}</div>
            <div className="text-xs text-slate-500 mt-0.5">{f.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section>
      <div className="mb-4">
        <h2 className="text-xl md:text-2xl font-bold text-slate-900">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function PricingTiers() {
  const { PRICING_TIERS } = usePlaybook();
  return (
    <Section
      title="Managed IT Pricing"
      subtitle="Three tiers. All include the value-adds below."
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PRICING_TIERS.map((t) => (
          <div
            key={t.name}
            className={`card p-5 relative ${
              t.recommended ? 'ring-2 ring-b1s-500' : ''
            }`}
          >
            {t.recommended && (
              <span className="absolute -top-2 right-4 chip bg-b1s-500 text-white">
                Recommended
              </span>
            )}
            <div className="text-sm font-semibold text-brand-700">{t.name}</div>
            <div className="text-2xl font-bold mt-1">
              {t.monthlyKESLabel || KES(t.monthlyKES)}
              <span className="text-sm font-normal text-slate-500"> / month</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">{t.headline}</div>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
              {t.bullets.map((b) => (
                <li key={b} className="flex gap-2">
                  <span className="text-b1s-500 font-bold">✓</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-3 border-t border-slate-100 text-xs text-slate-500">
              <span className="font-semibold text-slate-700">Best for: </span>
              {t.bestFor}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function ValueAdds() {
  const { VALUE_ADDS } = usePlaybook();
  return (
    <Section
      title="Value-Adds in Every Retainer"
      subtitle="Bundled into Silver, Gold, and Platinum — no upsell."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {VALUE_ADDS.map((v) => (
          <div key={v.title} className="card p-4">
            <div className="font-semibold text-sm text-brand-700">{v.title}</div>
            <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{v.body}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function IcpGrid() {
  const { ICP } = usePlaybook();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {ICP.map((i) => (
        <div key={i.segment} className="card p-4">
          <div className="font-semibold text-sm text-slate-900">{i.segment}</div>
          <p className="text-xs text-slate-600 mt-1.5">{i.hook}</p>
        </div>
      ))}
    </div>
  );
}

function TrustedBy() {
  const { TRUSTED_BY } = usePlaybook();
  return (
    <div className="flex flex-wrap gap-2">
      {TRUSTED_BY.map((c) => (
        <span
          key={c}
          className="chip bg-brand-50 text-brand-700 px-3 py-1.5 border border-brand-100"
        >
          {c}
        </span>
      ))}
    </div>
  );
}

function Objections() {
  const { OBJECTIONS } = usePlaybook();
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-2">
      {OBJECTIONS.map((o, idx) => {
        const isOpen = open === idx;
        return (
          <div key={idx} className="card overflow-hidden">
            <button
              onClick={() => setOpen(isOpen ? null : idx)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
            >
              <span className="font-semibold text-sm text-slate-900">{o.objection}</span>
              <span className="text-slate-400 text-lg">{isOpen ? '−' : '+'}</span>
            </button>
            {isOpen && (
              <div className="px-4 pb-4 text-sm text-slate-700 border-t border-slate-100 pt-3 leading-relaxed">
                {o.response}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* --- Templates with variable substitution --- */

function EmailTemplates() {
  const { EMAIL_TEMPLATES } = usePlaybook();
  const [activeId, setActiveId] = useState(EMAIL_TEMPLATES[0]?.id);
  const tpl = EMAIL_TEMPLATES.find((t) => t.id === activeId) || EMAIL_TEMPLATES[0];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
      <div className="space-y-1">
        {EMAIL_TEMPLATES.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveId(t.id)}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm border transition ${
              t.id === tpl?.id
                ? 'bg-brand-700 text-white border-brand-700'
                : 'bg-white border-slate-200 text-slate-700 hover:border-brand-300'
            }`}
          >
            {t.name}
          </button>
        ))}
      </div>
      <TemplateEditor template={tpl} kind="email" />
    </div>
  );
}

function CallScripts() {
  const { CALL_SCRIPTS } = usePlaybook();
  const [activeId, setActiveId] = useState(CALL_SCRIPTS[0]?.id);
  const tpl = CALL_SCRIPTS.find((t) => t.id === activeId) || CALL_SCRIPTS[0];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
      <div className="space-y-1">
        {CALL_SCRIPTS.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveId(t.id)}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm border transition ${
              t.id === tpl?.id
                ? 'bg-brand-700 text-white border-brand-700'
                : 'bg-white border-slate-200 text-slate-700 hover:border-brand-300'
            }`}
          >
            {t.name}
          </button>
        ))}
      </div>
      <TemplateEditor template={tpl} kind="call" />
    </div>
  );
}

function TemplateEditor({ template, kind }) {
  const variables = useMemo(() => {
    const vars = new Set();
    if (template.subject) extractVars(template.subject).forEach((v) => vars.add(v));
    extractVars(template.body).forEach((v) => vars.add(v));
    return Array.from(vars);
  }, [template]);

  const [values, setValues] = useState({});
  const [copiedField, setCopiedField] = useState('');

  const renderedSubject = template.subject ? applyVars(template.subject, values) : '';
  const renderedBody = applyVars(template.body, values);

  async function doCopy(field, text) {
    const ok = await copy(text);
    if (ok) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(''), 1500);
    }
  }

  function fillSample() {
    setValues({
      first_name: 'James',
      your_name: 'Mike Mbiyu',
      company: 'Acme Logistics Ltd',
      industry: 'logistics',
      industry_segment: 'logistics SMEs',
      previous_subject: 'Free IT audit for your team',
      audit_date: 'Tue 5 May, 10:00 AM'
    });
  }

  function clearValues() {
    setValues({});
  }

  return (
    <div className="card p-4">
      {variables.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              Variables
            </div>
            <div className="flex gap-2">
              <button onClick={fillSample} className="text-xs text-brand-700 hover:underline">
                Fill sample
              </button>
              <button onClick={clearValues} className="text-xs text-slate-500 hover:underline">
                Clear
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {variables.map((v) => (
              <div key={v}>
                <label className="label">{prettifyVar(v)}</label>
                <input
                  className="input"
                  value={values[v] || ''}
                  onChange={(e) => setValues({ ...values, [v]: e.target.value })}
                  placeholder={`{{${v}}}`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {template.subject && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              Subject
            </div>
            <button
              onClick={() => doCopy('subject', renderedSubject)}
              className="text-xs text-brand-700 hover:underline"
            >
              {copiedField === 'subject' ? '✓ Copied' : 'Copy subject'}
            </button>
          </div>
          <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium">
            {renderedSubject}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
            {kind === 'email' ? 'Body' : 'Script'}
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => doCopy('body', renderedBody)}
              className="text-xs text-brand-700 hover:underline"
            >
              {copiedField === 'body' ? '✓ Copied' : `Copy ${kind === 'email' ? 'body' : 'script'}`}
            </button>
            {template.subject && (
              <button
                onClick={() =>
                  doCopy('all', `Subject: ${renderedSubject}\n\n${renderedBody}`)
                }
                className="text-xs text-brand-700 hover:underline"
              >
                {copiedField === 'all' ? '✓ Copied' : 'Copy all'}
              </button>
            )}
          </div>
        </div>
        <pre className="whitespace-pre-wrap font-sans text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-lg p-3 leading-relaxed">
          {renderedBody}
        </pre>
      </div>
    </div>
  );
}

function ContactStrip() {
  const { COMPANY } = usePlaybook();
  return (
    <div className="card p-5 bg-brand-700 text-white border-brand-700">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="font-bold text-lg">{COMPANY.name}</div>
          <div className="text-brand-50/80 text-sm">{COMPANY.slogan}</div>
        </div>
        <div className="text-sm text-brand-50/90 space-y-0.5">
          <div>📍 {COMPANY.address}</div>
          <div>
            📞 <a className="hover:underline" href={`tel:${COMPANY.phone}`}>{COMPANY.phone}</a>
            {'  ·  '}
            ✉️ <a className="hover:underline" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
          </div>
          <div>🌐 <a className="hover:underline" href={COMPANY.website} target="_blank" rel="noreferrer">binaryone.co.ke</a></div>
        </div>
      </div>
    </div>
  );
}
